import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { useLang } from "@/context/LanguageContext";
import { awards } from "@/data/awards";
import { Award, ArrowUpRight, Sparkles, Quote } from "lucide-react";

const Awards = () => {
  const { t, lang } = useLang();

  return (
    <div data-testid="awards-page" className="relative">
      {/* HERO */}
      <section className="relative pt-40 pb-24 md:pt-52 md:pb-32 overflow-hidden">
        <div className="absolute inset-0">
          {/* radial gold + noise */}
          <div className="absolute -top-32 start-1/2 -translate-x-1/2 w-[70rem] h-[70rem] rounded-full blur-[160px] opacity-25 bg-[#E6A95B]" />
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,_transparent_0%,_rgba(10,10,12,0.6)_60%,_#0A0A0C_100%)]" />
          <div
            className="absolute inset-0 opacity-[0.07] mix-blend-overlay"
            style={{
              backgroundImage:
                "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='200' height='200'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.9'/></filter><rect width='100%' height='100%' filter='url(%23n)'/></svg>\")",
            }}
          />
        </div>

        <div className="relative container-x max-w-5xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-[#E6A95B]/40 bg-black/40 backdrop-blur text-[#E6A95B] text-xs uppercase tracking-[0.28em] mb-8"
          >
            <Award className="w-4 h-4" /> {t("awards.eyebrow")}
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.05 }}
            className="font-display font-black leading-[1.1] tracking-tight text-transparent bg-clip-text bg-gradient-to-br from-[#F6D28C] via-[#E6A95B] to-[#B7822F] text-4xl sm:text-5xl md:text-6xl lg:text-7xl drop-shadow-[0_2px_30px_rgba(230,169,91,0.35)]"
          >
            {t("awards.title")}
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.15 }}
            className="mt-8 text-white/75 text-lg md:text-xl leading-relaxed max-w-3xl"
          >
            {t("awards.lead")}
          </motion.p>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 0.4 }}
            className="mt-12 flex flex-wrap items-center gap-4 md:gap-6"
          >
            <div className="flex items-center gap-3 px-5 py-3 rounded-full border border-white/10 bg-[#0d0d10]">
              <Sparkles className="w-4 h-4 text-[#E6A95B]" />
              <span className="text-sm">{t("awards.stat1")}</span>
            </div>
            <div className="h-px flex-1 min-w-[40px] bg-gradient-to-r from-[#E6A95B]/50 via-white/10 to-transparent" />
            <div className="text-white/50 text-sm">{t("awards.stat2")}</div>
          </motion.div>
        </div>
      </section>

      {/* AWARDS LIST — alternating cinematic rows with fade-in on scroll */}
      <section className="pb-24 md:pb-32">
        <div className="container-x space-y-24 md:space-y-32">
          {awards.map((a, i) => {
            const flip = i % 2 === 1;
            return (
              <motion.article
                key={a.id}
                initial={{ opacity: 0, y: 60 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.2 }}
                transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
                data-testid={`award-${a.id}`}
                className={`grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-center ${
                  flip ? "" : ""
                }`}
              >
                {/* Trophy image with radial gold halo */}
                <div className={`lg:col-span-5 ${flip ? "lg:order-2" : "lg:order-1"}`}>
                  <div className="relative">
                    <div className="absolute -inset-6 md:-inset-10 rounded-full blur-3xl opacity-40 bg-gradient-radial from-[#E6A95B] via-[#B7822F]/40 to-transparent pointer-events-none" />
                    <motion.div
                      initial={{ scale: 0.95, opacity: 0 }}
                      whileInView={{ scale: 1, opacity: 1 }}
                      viewport={{ once: true, amount: 0.4 }}
                      transition={{ duration: 1.1, ease: "easeOut" }}
                      className="relative rounded-2xl overflow-hidden border border-[#E6A95B]/20 bg-gradient-to-b from-black/60 via-[#0d0d10] to-black/70 p-1.5 md:p-2.5 shadow-[0_30px_80px_-30px_rgba(230,169,91,0.35)]"
                    >
                      <div className="aspect-[4/5] w-full flex items-center justify-center overflow-hidden">
                        <img
                          src={a.image}
                          alt={a.title[lang]}
                          className={`w-full h-full object-contain drop-shadow-[0_20px_40px_rgba(0,0,0,0.9)] ${
                            a.zoom ? "scale-[1.35]" : "scale-110"
                          }`}
                          loading="lazy"
                        />
                      </div>
                      <div className="absolute inset-x-0 bottom-0 h-16 bg-gradient-to-t from-[#0A0A0C] to-transparent pointer-events-none" />
                    </motion.div>
                  </div>
                </div>

                {/* Text side */}
                <div className={`lg:col-span-7 ${flip ? "lg:order-1" : "lg:order-2"}`}>
                  <div className="flex items-center gap-3 mb-4">
                    <span className="font-display text-[#E6A95B] text-4xl md:text-5xl leading-none">
                      {String(i + 1).padStart(2, "0")}
                    </span>
                    <div className="h-px flex-1 bg-gradient-to-r from-[#E6A95B]/40 to-transparent" />
                    {a.year && (
                      <span className="text-white/50 text-sm font-medium tracking-widest">
                        {a.year}
                      </span>
                    )}
                  </div>

                  <div className="overline mb-3">{a.issuer[lang]}</div>
                  <h2 className="display-h2 leading-tight bg-clip-text text-transparent bg-gradient-to-br from-white via-white to-[#E6A95B]/80">
                    {a.title[lang]}
                  </h2>

                  <div className="mt-6 flex items-start gap-3 rounded-xl border border-[#E6A95B]/15 bg-[#0d0d10]/80 backdrop-blur p-5 md:p-6">
                    <Quote className="w-5 h-5 text-[#E6A95B] shrink-0 mt-1" />
                    <p className="text-white/80 text-sm md:text-base leading-relaxed italic">
                      {a.inscription[lang]}
                    </p>
                  </div>

                  <p className="mt-7 text-white/70 text-base md:text-lg leading-[1.9]">
                    {a.citation[lang]}
                  </p>
                </div>
              </motion.article>
            );
          })}
        </div>
      </section>

      {/* CTA */}
      <section className="relative py-24 md:py-32 border-t border-white/5 overflow-hidden">
        <div className="absolute inset-0 opacity-25 pointer-events-none">
          <img src="/bg/studio.jpg" alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-b from-[#0A0A0C]/85 via-[#0A0A0C]/85 to-[#0A0A0C]" />
        </div>
        <div className="relative container-x max-w-4xl mx-auto text-center">
          <div className="text-[#E6A95B] uppercase tracking-[0.32em] text-sm md:text-base font-medium mb-6">
            {t("awards.ctaEyebrow")}
          </div>
          <h2 className="font-display italic leading-[1.15] tracking-tight text-4xl sm:text-5xl md:text-6xl lg:text-7xl bg-clip-text text-transparent bg-gradient-to-br from-white via-white to-[#E6A95B]/80">
            {t("awards.ctaTitle")}
          </h2>
          <div className="mt-12">
            <Link to="/contact" data-testid="awards-cta" className="btn-primary inline-flex items-center gap-2 text-base md:text-lg px-8 py-4">
              {t("awards.ctaBtn")} <ArrowUpRight className="w-5 h-5" />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Awards;
